import Fuse from 'fuse.js'
import { ANSWER, CHECKLIST, CHECKLIST_ITEM, DISPLAY_TEXT, FOR_EACH_ANSWER, SEARCH, SEARCH_TEXT, FILTER, QUESTION, CATEGORY, INFO, TEXT } from './nodetypes';
import Logic from './logic';
import { KEYWORD_SEARCH_ATTR, FULL_TEXT_SEARCH_ATTR, LOCATION_ATTR, THRESHOLD_ATTR, DISTANCE_ATTR, MIN_MATCH_CHAR_LENGTH_ATTR } from './nodeattributes';

export default class SearchLogic extends Logic {
  constructor(_text, _attributes, _children, _owner) {
    super(_text, _attributes, _children, _owner);
    this.__defineNodeAttribute('searchType', [{ name: 'Keyword Search', value: KEYWORD_SEARCH_ATTR, default: true }, { name: 'Full Text Search', value: FULL_TEXT_SEARCH_ATTR }]);
  }
  clone() {
    return new SearchLogic(this.text, this.attributes, this.children.map((c) => c.clone()), this.owner);
  }
  onNodeUpdated(_node) {
    super.onNodeUpdated(_node);
    if (_node && _node.isOneOfTypes([SEARCH_TEXT])) {
      this.searchInput = _node;
      this.update(_node);
    }
  }
  get location() {
    const location = this.attributes.find((a) => a.includes(LOCATION_ATTR));
    if (location) return Math.max(parseFloat(location.split('=')[1]), 0);
    return 0;
  }
  get threshold() {
    const threshold = this.attributes.find((a) => a.includes(THRESHOLD_ATTR));
    if (threshold) return Math.max(parseFloat(threshold.split('=')[1]), 0);
    return 0.1;
  }
  get distance() {
    const distance = this.attributes.find((a) => a.includes(DISTANCE_ATTR));
    if (distance) return Math.max(parseFloat(distance.split('=')[1]), 0);
    return 100;
  }
  get minMatchCharLength() {
    const minMatchCharLength = this.attributes.find((a) => a.includes(MIN_MATCH_CHAR_LENGTH_ATTR));
    if (minMatchCharLength) return Math.max(parseInt(minMatchCharLength.split('=')[1], 10), 0);
    return 2;
  }
  updateLocation(_location) {
    if (_location < 0 || _location > 100) return this.__attributeChanged();
    this.attributes = this.attributes.filter((_attr) => !_attr.includes(LOCATION_ATTR));
    if (!_location) return this.__attributeChanged();
    return this.addAttribute(`${LOCATION_ATTR}=${_location}`);
  }
  updateThreshold(_threshold) {
    if (_threshold < 0 || _threshold > 100) return this.__attributeChanged();
    this.attributes = this.attributes.filter((_attr) => !_attr.includes(THRESHOLD_ATTR));
    if (!_threshold) return this.__attributeChanged();
    return this.addAttribute(`${THRESHOLD_ATTR}=${_threshold}`);
  }
  updateDistance(_distance) {
    if (_distance < 0 || _distance > 100) return this.__attributeChanged();
    this.attributes = this.attributes.filter((_attr) => !_attr.includes(DISTANCE_ATTR));
    if (!_distance) return this.__attributeChanged();
    return this.addAttribute(`${DISTANCE_ATTR}=${_distance}`);
  }
  updateMinCharMatchLength(_minCharMatchLength) {
    if (_minCharMatchLength < 0 || _minCharMatchLength > 30) return this.__attributeChanged();
    this.attributes = this.attributes.filter((_attr) => !_attr.includes(MIN_MATCH_CHAR_LENGTH_ATTR));
    if (!_minCharMatchLength) return this.__attributeChanged();
    return this.addAttribute(`${MIN_MATCH_CHAR_LENGTH_ATTR}=${_minCharMatchLength}`);
  }
  __update = () => {
    if (this.exclude) return;
    if (!this.searchInput) return;
    if (this.filterTimeout) clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.filterTimeout = null;
      let excludeWords = [];
      this.keywords.forEach((_keywords) => {
        if (!_keywords.exclude) return;
        excludeWords = [...excludeWords, ..._keywords.text
          .split(_keywords.delimiterString)
          .map((_k) => _k.toLowerCase().trim())
          .filter((k) => k)];
      });
      
      const fullTextSearch = this.attributes.includes(FULL_TEXT_SEARCH_ATTR);
      const searchInputArray = __getSanitizedTextArray(this.searchInput.text);

      // Find all the filters and questions
      const inputs = this.getAllInputs(this);

      /**
       * KeywordsFoundBySearch
       */
      this.keywordsFoundBySearch = {};

      const options = {
        isCaseSensitive: false,
        includeScore: true,
        shouldSort: true,
        includeMatches: false,
        findAllMatches: false,
        minMatchCharLength: this.minMatchCharLength,
        location: this.location,
        threshold: fullTextSearch ? this.threshold : 0,
        distance: this.distance,
        useExtendedSearch: false,
        ignoreLocation: false,
        ignoreFieldNorm: false,
      };
      const fuse = {
        1: (new Fuse(searchInputArray, options)),
      }

      // CreateLength is used to handle using specified keywords and allowing for multi-word keywords
      function createLength(length) {
        const newArray = searchInputArray.map((s, i) => {
          let string = s;
          for (let j = 1; j < length; j += 1) {
            string += searchInputArray[i + j] && ` ${searchInputArray[i + j]}` || '';
          }
          return string;
        });
        return new Fuse(newArray, options);
      }

      const __findKeywordInSearchInputText = (_keyword) => {
        if (fullTextSearch && stoplist.has(_keyword)) return null;
        const length = _keyword.split(" ").length;
        if (!fuse[length]) fuse[length] = createLength(length)
        const fuseResult = fuse[length].search(_keyword);
        if (fuseResult && fuseResult[0] && (fullTextSearch || fuseResult[0].score === 0)) {
          return _keyword;
        }
        return null;
      };
      const __getNodeKeywords = (_node) => {
        let keywords = [];
        _node.keywords.forEach((_keywords) => {
          keywords = [...keywords, ..._keywords.keywordArray
            .map((_k) => _k.toLowerCase().trim())
            .filter((k) => k && !excludeWords.includes(k))];
        });
        return fullTextSearch ? __getSanitizedTextArray(_node.text) : keywords;
      };
      const __getNodeKeywordsFound = (_node) => {
        if (!_node.keywordsFoundBySearch) return;
        // Searching by Keywords
        // Get array of keywords that a node has
        const keywords = __getNodeKeywords(_node);

        // Find keywords in the keywords array
        const keywordsFound = keywords.filter(__findKeywordInSearchInputText);

        // Keep cumulative track at filter and search levels
        keywordsFound.forEach((k) => {
          this.keywordsFoundBySearch[k] = (this.keywordsFoundBySearch[k] || 0) + 1;
          _node.keywordsFoundBySearch.add(k);
          _node.parents.forEach((p) => p.keywordsFoundBySearch && p.keywordsFoundBySearch.add && p.keywordsFoundBySearch.add(k));
        });
        _node.emitStateUpdate();
      };

      const __recursivelyGetKeywords = (node) => {
        if (this.isExcludeBoundary(node)) return;
        if ([FOR_EACH_ANSWER].includes(node.attributes[0])) return;
        node.keywordsFoundBySearch = new Set();
        node.children.forEach(__recursivelyGetKeywords);
        if (node.isOneOfTypes([ANSWER, CATEGORY, CHECKLIST, CHECKLIST_ITEM, DISPLAY_TEXT, FILTER, INFO, QUESTION, TEXT])) __getNodeKeywordsFound(node);
      }

      this.children.forEach(__recursivelyGetKeywords);

      // Interate through Inputs
      inputs.forEach((_input) => {
        _input.keywordsFoundBySearch = new Set(Object.keys(this.keywordsFoundBySearch));
        _input.emitStateUpdate();
      });

      // Update search
      this.__updateTreeOnNodeUpdated();
    }, 1000);
  }
  get input() {
    return this.children.find((_child) => _child.isType(SEARCH));
  }
  getAllInputs(node) {
    let totalFilters = [];
    node.children.forEach((_child) => {
      if (_child.isType(SEARCH)) totalFilters.push(_child);
      if (_child.isOneOfTypes([ANSWER, CHECKLIST_ITEM]) && !_child.checked) return;
      if (this.isExcludeBoundary(_child)) return;
      if ([FOR_EACH_ANSWER].includes(_child.attributes[0])) return;
      totalFilters = [...totalFilters, ...this.getAllInputs(_child)];
    });
    return totalFilters;
  }
}
function __getSanitizedTextArray(text) {
  const parser = new DOMParser();
  const dom = parser.parseFromString(text, 'text/html');
  return __recursivelyGetTextNodes(dom.body);
}
function __recursivelyGetTextNodes(node) {
  let array = [];
  [...node.childNodes].forEach((c) => {
    if (c.nodeName === '#text') array = [...array, ...c.textContent.toLowerCase().split(/([\w'-]+)/g).map((k) => k.trim()).filter((k) => k)];
    else array = [...array, ...__recursivelyGetTextNodes(c)];
  });
  return array;
}
const stoplist = new Set(["","","a","able","about","above","abst","accordance","according","accordingly","across","act","actually","added","adj","affected","affecting","affects","after","afterwards","again","against","ah","all","almost","alone","along","already","also","although","always","am","among","amongst","an","and","announce","another","any","anybody","anyhow","anymore","anyone","anything","anyway","anyways","anywhere","apparently","approximately","are","aren","arent","arise","around","as","aside","ask","asking","at","auth","available","away","awfully","b","back","be","became","because","become","becomes","becoming","been","before","beforehand","begin","beginning","beginnings","begins","behind","being","believe","below","beside","besides","between","beyond","biol","both","brief","briefly","but","by","c","ca","came","can","cannot","can","t","cause","causes","certain","certainly","co","com","come","comes","contain","containing","contains","could","couldnt","d","date","did","didn","t","different","do","does","doesn","t","doing","done","don","t","down","downwards","due","during","e","each","ed","edu","effect","eg","eight","eighty","either","else","elsewhere","end","ending","enough","especially","et","et","al","etc","even","ever","every","everybody","everyone","everything","everywhere","ex","except","f","far","few","ff","fifth","first","five","fix","followed","following","follows","for","former","formerly","forth","found","four","from","further","furthermore","g","gave","get","gets","getting","give","given","gives","giving","go","goes","gone","got","gotten","h","had","happens","hardly","has","hasn","t","have","haven","t","having","he","hed","hence","her","here","hereafter","hereby","herein","heres","hereupon","hers","herself","hes","hi","hid","him","himself","his","hither","home","how","howbeit","however","hundred","i","id","ie","if","i","ll","im","immediate","immediately","importance","important","in","inc","indeed","index","information","instead","into","invention","inward","is","isn","t","it","itd","it","ll","its","itself","i","ve","j","just","k","keep","","","keeps","kept","kg","km","know","known","knows","l","largely","last","lately","later","latter","latterly","least","less","lest","let","lets","like","liked","likely","line","little","","ll","look","looking","looks","ltd","m","made","mainly","make","makes","many","may","maybe","me","mean","means","meantime","meanwhile","merely","mg","might","million","miss","ml","more","moreover","most","mostly","mr","mrs","much","mug","must","my","myself","n","na","name","namely","nay","nd","near","nearly","necessarily","necessary","need","needs","neither","never","nevertheless","new","next","nine","ninety","no","nobody","non","none","nonetheless","noone","nor","normally","nos","not","noted","nothing","now","nowhere","o","obtain","obtained","obviously","of","off","often","oh","ok","okay","old","omitted","on","once","one","ones","only","onto","or","ord","other","others","otherwise","ought","our","ours","ourselves","out","outside","over","overall","owing","own","p","page","pages","part","particular","particularly","past","per","perhaps","placed","please","plus","poorly","possible","possibly","potentially","pp","predominantly","present","previously","primarily","probably","promptly","proud","provides","put","q","que","quickly","quite","qv","r","ran","rather","rd","re","readily","really","recent","recently","ref","refs","regarding","regardless","regards","related","relatively","research","respectively","resulted","resulting","results","right","run","s","said","same","saw","say","saying","says","sec","section","see","seeing","seem","seemed","seeming","seems","seen","self","selves","sent","seven","several","shall","she","shed","she","ll","shes","should","shouldn","t","show","showed","shown","showns","shows","significant","significantly","similar","similarly","since","six","slightly","so","some","somebody","somehow","someone","somethan","something","sometime","sometimes","somewhat","somewhere","soon","sorry","specifically","specified","specify","specifying","still","stop","strongly","sub","substantially","successfully","such","sufficiently","suggest","sup","sure","","","t","take","taken","taking","tell","tends","th","than","thank","thanks","thanx","that","that","ll","thats","that","ve","the","their","theirs","them","themselves","then","thence","there","thereafter","thereby","thered","therefore","therein","there","ll","thereof","therere","theres","thereto","thereupon","there","ve","these","they","theyd","they","ll","theyre","they","ve","think","this","those","thou","though","thoughh","thousand","throug","through","throughout","thru","thus","til","tip","to","together","too","took","toward","towards","tried","tries","truly","try","trying","ts","twice","two","u","un","under","unfortunately","unless","unlike","unlikely","until","unto","up","upon","ups","us","use","used","useful","usefully","usefulness","uses","using","usually","v","value","various","","ve","very","via","viz","vol","vols","vs","w","want","wants","was","wasnt","way","we","wed","welcome","we","ll","went","were","werent","we","ve","what","whatever","what","ll","whats","when","whence","whenever","where","whereafter","whereas","whereby","wherein","wheres","whereupon","wherever","whether","which","while","whim","whither","who","whod","whoever","whole","who","ll","whom","whomever","whos","whose","why","widely","willing","wish","with","within","without","wont","words","world","would","wouldnt","www","x","y","yes","yet","you","youd","you","ll","your","youre","yours","yourself","yourselves","you","ve","z","zero","",""]);
